<template>
    <v-dialog v-model="state.open" max-width="480px" persistent>
        <v-card>
            <v-card-title>
                <span class="headline">{{ $t("operatingInstructions.edit") }}</span>
            </v-card-title>

            <v-card-text>
                <v-container>
                    <v-text-field v-model="form.tag" :label="$t('betriebsanweisung.tag')"
                        hide-details="auto" data-cy="edit-tag" />

                    <v-select v-model="form.color" :items="colorOptions"
                        :label="$t('betriebsanweisung.color')" hide-details="auto" clearable
                        item-text="label" item-value="value" data-cy="edit-color">
                        <template #selection="{ item }">
                            <div class="d-flex align-center">
                                <span :style="dotStyle(item.value)"></span> {{ item.label }}
                            </div>
                        </template>
                        <template #item="{ item }">
                            <div class="d-flex align-center">
                                <span :style="dotStyle(item.value)"></span> {{ item.label }}
                            </div>
                        </template>
                    </v-select>
                    <div class="mt-2" v-if="labels.length">
                        <div class="subtitle-2 mb-1">
                            {{ $t("operatingInstructions.existingLabels") }}
                        </div>
                        <div class="label-list">
                            <div v-for="label in labels" :key="label._id" class="label-pill"
                                :style="{ backgroundColor: label.color || 'transparent' }" @click="applyLabel(label)">
                                <span class="label-pill-text">{{ label.tag }}</span>
                                <v-icon small class="ml-2" @click.stop="deleteLabel(label)" data-cy="delete-label">
                                    delete
                                </v-icon>
                            </div>
                        </div>
                    </div>
                </v-container>
            </v-card-text>

            <v-card-actions>
                <div class="flex-grow-1"></div>
                <v-btn color="grey" outlined @click="close">
                    {{ $t('general.cancel') }}
                </v-btn>
                <v-btn color="primary" @click="save" :loading="saving" data-cy="save-meta">
                    {{ $t('general.save') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: "editOperatingInstructionDialog",
    props: {
        state: { type: Object, required: true },
        item: { type: Object, required: true },
    },
    data() {
        return {
            saving: false,
            form: {
                tag: "",
                color: "",
            },
            colorOptions: [
                { label: "Red", value: "red" },
                { label: "Green", value: "green" },
                { label: "Yellow", value: "gold" },
                { label: "Blue", value: "deepskyblue" },
                { label: "Orange", value: "orange" },
                { label: "Purple", value: "purple" },
            ],
            labels: [],
        };
    },
    computed: {
        label() {
            const l = this.item?.operatingInstructionLabel;
            return l && typeof l === "object" ? l : null;
        },
        labelId() {
            const l = this.item?.operatingInstructionLabel;
            if (!l) return null;
            return typeof l === "object" ? l._id : l;
        },
    },
    watch: {
        state: {
            handler(v) {
                if (v.open && this.item) {
                    this.prefillFromLabel();
                }
            },
            deep: true,
            immediate: true,
        },
        item: {
            handler() {
                if (this.state.open && this.item) {
                    this.prefillFromLabel();
                }
            },
            immediate: true,
        },
    },

    async mounted() {
        await this.fetchLabels();
        this.$root.$on("operatingInstructionLabelsUpdated", this.fetchLabels);
    },
    beforeDestroy() {
        this.$root.$off("operatingInstructionLabelsUpdated", this.fetchLabels);
    },

    methods: {
        async fetchLabels() {
            try {
                const { data } = await this.$store.dispatch("getOperatingInstructionLabels");
                this.labels = Array.isArray(data) ? data : [];
            } catch (e) {
                console.error("Failed to load labels", e);
            }
        },

        prefillFromLabel() {
            if (this.label) {
                this.form.tag = this.label.tag || "";
                this.form.color = this.label.color || "";
            } else {
                this.form.tag = "";
                this.form.color = "";
            }
        },

        dotStyle(color) {
            return {
                display: "inline-block",
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                marginRight: "8px",
                backgroundColor: color || "transparent",
            };
        },

        applyLabel(label) {
            this.form.tag = label.tag || "";
            this.form.color = label.color || "";
        },

        async deleteLabel(label) {
            if (!label || !label._id) return;

            try {
                await this.$store.dispatch("deleteOperatingInstructionLabel", {
                    id: label._id,
                });

                this.labels = this.labels.filter((l) => l._id !== label._id);

                this.$root.$emit("operatingInstructionLabelsUpdated");

                if (this.labelId === label._id) {
                    this.form.tag = "";
                    this.form.color = "";
                }
                this.$emit("label-deleted");
            } catch (e) {
                console.error("Failed to delete label", e);
                this.$store.commit(
                    "CREATE_SNACKBAR",
                    this.$t("general.error") || "Fehler"
                );
            }
        },

        close() {
            this.state.open = false;
        },

        async save() {
            if (!this.item || (!this.item._id && !this.item.id)) return;

            this.saving = true;

            const tag = (this.form.tag || "").trim();
            const color = this.form.color || "";

            try {
                if (this.labelId) {
                    await this.$store.dispatch("updateOperatingInstructionLabel", {
                        id: this.labelId,
                        tag,
                        color,
                    });
                } else {
                    if (!tag && !color) {
                        this.$store.commit(
                            "CREATE_SNACKBAR",
                            this.$t("operatingInstructions.noLabelData") ||
                            "Bitte Tag oder Farbe angeben."
                        );
                        return;
                    }

                    const newLabel = await this.$store.dispatch(
                        "createOperatingInstructionLabel",
                        { tag, color }
                    );
                    const instructionId = this.item.id || this.item._id;

                    await this.$store.dispatch("updateOperatingInstruction", {
                        id: instructionId,
                        operatingInstructionLabel: newLabel._id,
                    });
                }

                this.$root.$emit("operatingInstructionLabelsUpdated");

                this.$emit("saved");
                this.close();
            } catch (e) {
                console.error("Update failed:", e);
                this.$store.commit(
                    "CREATE_SNACKBAR",
                    this.$t("general.error") || "Fehler"
                );
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.label-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.label-pill {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 16px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    cursor: pointer;
    font-size: 0.875rem;
    color: #fff;
}

.label-pill-text {
    white-space: nowrap;
}
</style>
